import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '@core/types';
import {
  CommonArea,
  CommonAreaCreate,
  CommonAreaUpdate,
  CommonAreaSchedule,
  CommonAreaScheduleCreate,
  CommonAreaScheduleException,
  CommonAreaScheduleExceptionCreate,
  Reservation,
  ReservationCreate,
  ReservationFilter,
  MaintenanceTicket,
  MaintenanceTicketCreate,
  MaintenanceTicketUpdate,
  MaintenanceTicketFilter,
  InventoryItem,
  InventoryItemCreate,
  InventoryItemUpdate,
  InventoryItemFilter,
  MovingRequest,
  MovingRequestCreate,
  MovingRequestUpdate,
  MovingRequestFilter,
} from '@core/types/operations.type';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';

@Injectable({ providedIn: 'root' })
export class OperationsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.api;

  // ===== Common Areas =====

  getCommonAreas(): Observable<ApiResponse<CommonArea[]>> {
    return this.http.get<ApiResponse<CommonArea[]>>(`${this.apiUrl}/common-areas`);
  }

  createCommonArea(data: CommonAreaCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/common-areas`, data);
  }

  updateCommonArea(data: CommonAreaUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/common-areas`, data);
  }

  deleteCommonArea(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/common-areas/${id}`);
  }

  // Schedules
  getCommonAreaSchedules(commonAreaId: string): Observable<ApiResponse<CommonAreaSchedule[]>> {
    return this.http.get<ApiResponse<CommonAreaSchedule[]>>(`${this.apiUrl}/common-areas/${commonAreaId}/schedules`);
  }

  addCommonAreaSchedule(commonAreaId: string, data: CommonAreaScheduleCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/common-areas/${commonAreaId}/schedules`, data);
  }

  deleteCommonAreaSchedule(commonAreaId: string, scheduleId: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/common-areas/${commonAreaId}/schedules/${scheduleId}`);
  }

  // Exceptions
  getCommonAreaExceptions(commonAreaId: string): Observable<ApiResponse<CommonAreaScheduleException[]>> {
    return this.http.get<ApiResponse<CommonAreaScheduleException[]>>(`${this.apiUrl}/common-areas/${commonAreaId}/exceptions`);
  }

  addCommonAreaException(commonAreaId: string, data: CommonAreaScheduleExceptionCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/common-areas/${commonAreaId}/exceptions`, data);
  }

  deleteCommonAreaException(commonAreaId: string, exceptionId: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/common-areas/${commonAreaId}/exceptions/${exceptionId}`);
  }

  // ===== Reservations =====

  getReservations(filter: ReservationFilter): Observable<ApiResponse<Reservation[]>> {
    let params = new HttpParams();
    if (filter.commonAreaId) params = params.set('commonAreaId', filter.commonAreaId);
    if (filter.from) params = params.set('from', filter.from);
    if (filter.to) params = params.set('to', filter.to);

    return this.http.get<ApiResponse<Reservation[]>>(`${this.apiUrl}/reservations`, { params });
  }

  createReservation(data: ReservationCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/reservations`, data);
  }

  deleteReservation(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/reservations/${id}`);
  }

  // ===== Maintenance Tickets =====

  getMaintenanceTicketsGrid(config: {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
    filter?: MaintenanceTicketFilter;
  }): Observable<ApiResponse<PaginationResult<MaintenanceTicket>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());
    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);
    const f = config.filter;
    if (f?.status !== undefined) params = params.set('filter.status', f.status.toString());
    if (f?.priority !== undefined) params = params.set('filter.priority', f.priority.toString());
    if (f?.unitId) params = params.set('filter.unitId', f.unitId);
    return this.http.get<ApiResponse<PaginationResult<MaintenanceTicket>>>(
      `${this.apiUrl}/maintenance-tickets/grid`,
      { params }
    );
  }

  getMaintenanceTicket(id: string): Observable<ApiResponse<MaintenanceTicket>> {
    return this.http.get<ApiResponse<MaintenanceTicket>>(`${this.apiUrl}/maintenance-tickets/${id}`);
  }

  createMaintenanceTicket(data: MaintenanceTicketCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/maintenance-tickets`, data);
  }

  updateMaintenanceTicket(data: MaintenanceTicketUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/maintenance-tickets`, data);
  }

  deleteMaintenanceTicket(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/maintenance-tickets/${id}`);
  }

  // ===== Inventory Items =====

  getInventoryItemsGrid(config: {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
    filter?: InventoryItemFilter;
  }): Observable<ApiResponse<PaginationResult<InventoryItem>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());
    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);
    const f = config.filter;
    if (f?.category) params = params.set('filter.category', f.category);
    return this.http.get<ApiResponse<PaginationResult<InventoryItem>>>(
      `${this.apiUrl}/inventory-items/grid`,
      { params }
    );
  }

  getInventoryItem(id: string): Observable<ApiResponse<InventoryItem>> {
    return this.http.get<ApiResponse<InventoryItem>>(`${this.apiUrl}/inventory-items/${id}`);
  }

  createInventoryItem(data: InventoryItemCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/inventory-items`, data);
  }

  updateInventoryItem(data: InventoryItemUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/inventory-items`, data);
  }

  deleteInventoryItem(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/inventory-items/${id}`);
  }

  // ===== Moving Requests =====

  getMovingRequestsGrid(config: {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
    filter?: MovingRequestFilter;
  }): Observable<ApiResponse<PaginationResult<MovingRequest>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());
    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);
    const f = config.filter;
    if (f?.status !== undefined) params = params.set('filter.status', f.status.toString());
    if (f?.isMovingIn !== undefined) params = params.set('filter.isMovingIn', String(f.isMovingIn));
    if (f?.unitId) params = params.set('filter.unitId', f.unitId);
    return this.http.get<ApiResponse<PaginationResult<MovingRequest>>>(
      `${this.apiUrl}/moving-requests/grid`,
      { params }
    );
  }

  getMovingRequest(id: string): Observable<ApiResponse<MovingRequest>> {
    return this.http.get<ApiResponse<MovingRequest>>(`${this.apiUrl}/moving-requests/${id}`);
  }

  createMovingRequest(data: MovingRequestCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/moving-requests`, data);
  }

  updateMovingRequest(data: MovingRequestUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/moving-requests`, data);
  }

  deleteMovingRequest(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/moving-requests/${id}`);
  }
}

